import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../lib/api";
import type { PaginatedResponse, Team } from "../../types";
import {
  Select,
  SelectContent,
  SelectEmpty,
  SelectItem,
  SelectSearchInput,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useSelectFilter } from "../ui/useSelectFilter";

/** Radix `Select.Item` forbids an empty-string value — same sentinel
 *  pattern as CountrySelect / the MatchesManager entity pickers. */
const UNSET = "__unset__";

export interface TeamPickerProps {
  value: string;
  onChange: (teamId: string) => void;
  disabled?: boolean;
}

interface TeamOption {
  value: string;
  label: string;
}

/**
 * Admin's team choice for the roster importer (EPIC-95 AC4, "the admin picks
 * a team (required)"). Fetches the full team list once (same pagination
 * loop as MatchesManager's `fetchTeams` — backend caps page_size at 100) and
 * reuses the app's one searchable-Select pattern (CountrySelect,
 * MatchesManager's EntityPicker) rather than a bespoke combobox.
 */
export default function TeamPicker({
  value,
  onChange,
  disabled,
}: TeamPickerProps) {
  const { t } = useTranslation("admin");
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(false);
        const allTeams: Team[] = [];
        let page = 1;
        const pageSize = 100;
        let hasMore = true;
        while (hasMore) {
          const data = await apiClient.get<PaginatedResponse<Team> | Team[]>(
            "/teams/",
            { params: { page, page_size: pageSize } },
          );
          if (Array.isArray(data)) {
            allTeams.push(...data);
            hasMore = false;
          } else {
            allTeams.push(...data.items);
            hasMore = allTeams.length < (data.total ?? data.items.length);
          }
          page += 1;
        }
        if (!cancelled) {
          setTeams(
            allTeams
              .map((team) => ({ value: team.team_id, label: team.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          );
        }
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedLabel = teams.find((opt) => opt.value === value)?.label;
  const { query, setQuery, visible } = useSelectFilter(teams);

  return (
    <div>
      <Select
        value={value === "" ? UNSET : value}
        onValueChange={(next) => onChange(next === UNSET ? "" : next)}
        disabled={disabled || loading}
      >
        <SelectTrigger
          aria-label={t("rosterImport.selectTeam")}
          data-testid="roster-import-team-select"
          className="w-full"
        >
          <SelectValue
            placeholder={loading ? t("loading") : t("rosterImport.selectTeam")}
          >
            {selectedLabel}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem
            value={UNSET}
            data-testid="roster-import-team-option-unset"
          >
            {t("rosterImport.selectTeam")}
          </SelectItem>
          <SelectSearchInput
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search")}
          />
          {visible.length === 0 && (
            <SelectEmpty>{t("pickerNoMatches")}</SelectEmpty>
          )}
          {visible.map((opt) => (
            <SelectItem
              key={opt.value}
              value={opt.value}
              data-testid={`roster-import-team-option-${opt.value}`}
            >
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {loadError && (
        <p className="mt-1 text-xs text-red-600">
          {t("rosterImport.teamsLoadError")}
        </p>
      )}
    </div>
  );
}
